// Account Settings Page - Account security and deletion
// Tabbed interface for organizing different settings categories
// Styled with Tailwind CSS following established design patterns

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useFocusManagement } from '../hooks/useFocusManagement';
import { LoadingSpinner } from '../components/ui';
import { useUserBadgeCount } from '../hooks/useUserBadges';
import { auth } from '../lib/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { DeleteAccountSection } from '../components/account/DeleteAccountSection';

type SettingsTab = 'account' | 'danger';

export function SettingsPage() {
  const { user, isEmailVerified } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>('account');
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const badgeCount = useUserBadgeCount();

  const { containerRef } = useFocusManagement({
    autoFocusFirst: true,
  });

  // Handle password reset
  const handlePasswordReset = async () => {
    if (!user?.email) return;

    setIsProcessing(true);
    setMessage(null);

    try {
      await sendPasswordResetEmail(auth, user.email);
      setMessage({
        type: 'success',
        text: 'Password reset email sent! Check your inbox.',
      });
    } catch (error) {
      console.error('Password reset error:', error);
      setMessage({
        type: 'error',
        text: 'Failed to send password reset email. Please try again.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!user) {
    return <LoadingSpinner fullScreen message="Loading settings..." />;
  }

  const tabs: { key: SettingsTab; label: string; icon: JSX.Element }[] = [
    {
      key: 'account',
      label: 'Account',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      key: 'danger',
      label: 'Danger Zone',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Account Settings
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/dashboard" className="btn-secondary">
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div
          ref={containerRef}
          className="flex flex-col lg:flex-row gap-4 lg:gap-8"
        >
          {/* Mobile Tab Bar */}
          <nav className="lg:hidden">
            <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.key
                      ? 'bg-sky-100 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span
                    className={
                      activeTab === tab.key
                        ? 'text-sky-600 dark:text-sky-400'
                        : 'text-gray-400'
                    }
                  >
                    {tab.icon}
                  </span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </nav>

          {/* Desktop Sidebar Navigation */}
          <nav className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="card p-2">
              <ul className="space-y-1">
                {tabs.map(tab => (
                  <li key={tab.key}>
                    <button
                      onClick={() => setActiveTab(tab.key)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === tab.key
                          ? 'bg-sky-100 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span
                        className={
                          activeTab === tab.key
                            ? 'text-sky-600 dark:text-sky-400'
                            : 'text-gray-400'
                        }
                      >
                        {tab.icon}
                      </span>
                      <span>{tab.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          {/* Settings Content */}
          <div className="flex-1">
            {/* Alert Messages */}
            {message && (
              <div
                className={`mb-6 rounded-lg p-4 ${
                  message.type === 'success'
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {message.type === 'success' ? (
                      <svg
                        className="h-5 w-5 text-green-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <p
                      className={`text-sm font-medium ${
                        message.type === 'success'
                          ? 'text-green-800 dark:text-green-200'
                          : 'text-red-800 dark:text-red-200'
                      }`}
                    >
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Account Settings Tab */}
            {activeTab === 'account' && (
              <div className="space-y-6">
                {/* Email Verification Status */}
                {!isEmailVerified && (
                  <div className="card bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                    <div className="card-content">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5 text-amber-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3 flex-1">
                          <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                            Email Not Verified
                          </h3>
                          <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                            Verify your email address.
                          </p>
                          <div className="mt-3">
                            <Link
                              to="/verify-email"
                              className="btn-sm bg-amber-600 hover:bg-amber-700 text-white"
                            >
                              Verify Email
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Account Information */}
                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">Account Information</h2>
                  </div>
                  <div className="card-content space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Address
                      </label>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 input bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                          {user.email}
                        </div>
                        {isEmailVerified && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
                            <svg
                              className="w-3 h-3 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Verified
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        User ID
                      </label>
                      <div className="input bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-mono text-sm">
                        {user.uid}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Account Created
                      </label>
                      <div className="input bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                        {new Date(user.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Password & Security */}
                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">Password & Security</h2>
                  </div>
                  <div className="card-content">
                    <button
                      onClick={() => void handlePasswordReset()}
                      disabled={isProcessing}
                      className="btn-primary"
                    >
                      {isProcessing ? 'Sending...' : 'Reset Password'}
                    </button>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      We&apos;ll send you an email with instructions to reset
                      your password
                    </p>
                  </div>
                </div>

                {/* Badge Overview */}
                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">Badges</h2>
                  </div>
                  <div className="card-content">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                            <svg
                              className="w-6 h-6 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M9.12 2.054l1.83 5.634a1 1 0 00.95.69h5.923c.969 0 1.371 1.24.588 1.81l-4.795 3.484a1 1 0 00-.364 1.118l1.83 5.634c.3.922-.755 1.688-1.538 1.118l-4.795-3.484a1 1 0 00-1.176 0l-4.795 3.484c-.783.57-1.838-.196-1.538-1.118l1.83-5.634a1 1 0 00-.364-1.118L.522 10.188c-.783-.57-.38-1.81.588-1.81h5.923a1 1 0 00.95-.69L9.12 2.054c.3-.922 1.46-.922 1.76 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Badges Earned
                          </h3>
                          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            {badgeCount}
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <Link to="/profile" className="btn-secondary text-sm">
                          View All Badges
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Danger Zone Tab */}
            {activeTab === 'danger' && (
              <div className="space-y-6">
                <div className="card border-red-200 dark:border-red-800">
                  <div className="card-header bg-red-50 dark:bg-red-900/20">
                    <h2 className="card-title text-red-800 dark:text-red-200">
                      Danger Zone
                    </h2>
                    <p className="card-description text-red-700 dark:text-red-300">
                      Irreversible and destructive actions
                    </p>
                  </div>
                  <div className="card-content">
                    <div className="space-y-6">
                      <DeleteAccountSection
                        heading="Delete Account"
                        description="Once you delete your account, there is no going back. All your data will be permanently removed."
                        actionLabel="Delete Account Permanently"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
